import { Avatar, TextField } from '@efexteam/components';
import { Spacings5 } from '@efexteam/design-tokens';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Autocomplete, Box, CircularProgress, Stack } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEmailValidation from './hooks/useEmailValidation';
import { IContact } from './types';

const EmailValidation = ({
  canContinue,
}: {
  canContinue: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const {
    contacts,
    results,
    loading,
    error,
    selectedContact,
    control,
    handleChange,
    shouldPerformSearch,
  } = useEmailValidation({ canContinue });

  const renderOptionLine = (
    props: React.HTMLAttributes<HTMLLIElement>,
    contact: IContact
  ) => {
    const entity = results.find(
      (opt) => opt.naturalPerson.id === contact.value
    );

    return (
      <Box
        component="li"
        className="px-3"
        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
        {...props}
      >
        <div className="flex space-x-2 items-center">
          <Avatar
            username={entity?.naturalPerson.firstName || t('newBusiness') || ''}
            size="sm"
          />
          <div>
            <p className="m-0 text-sm">
              {!entity ? `${t('newBusiness')}: ` : ''}
              {entity?.naturalPerson?.email ?? contact.label}
            </p>
            <p className="m-0 text-xs text-gray-400">
              {entity?.enterprise?.entityType ?? ''}
            </p>
          </div>
        </div>
      </Box>
    );
  };

  return (
    <Stack sx={{ gap: Spacings5 }}>
      <Controller
        control={control}
        name="email"
        render={({
          field: { value, ref },
          fieldState: { error: errorEmail },
        }) => (
          <Autocomplete
            freeSolo
            options={contacts}
            value={value}
            ref={ref}
            sx={{ '& .MuiInputBase-input': { padding: '0px !important' } }}
            getOptionLabel={(contact) => (contact as IContact).label}
            renderOption={(props, contact) => renderOptionLine(props, contact)}
            renderInput={(params) => (
              <TextField
                {...params}
                id="email"
                type="email"
                label={t('email')}
                error={!!errorEmail || !!error}
                helperText={
                  ((errorEmail as any)?.label.type === 'too_small' &&
                    t('emailValidation')) ||
                  ((errorEmail as any)?.type === 'pattern' &&
                    t('validateEmail')) ||
                  (error as any)?.response?.data?.detail
                }
                autoComplete="off"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                }}
              />
            )}
            onChange={handleChange}
            onInputChange={handleChange}
            onChangeCapture={shouldPerformSearch}
            noOptionsText={
              <div>
                <SearchOffIcon sx={{ width: 50, height: 50 }} />
                <p>{t('textSearchNotFound')}</p>
              </div>
            }
          />
        )}
      />
      {selectedContact &&
        selectedContact.value &&
        selectedContact.value !== 1000000000 && (
          <p>{t('memberExists', { email: selectedContact.label })}</p>
        )}
      <Controller
        control={control}
        name="userName"
        render={({ field, fieldState: { error: errorUserName } }) => (
          <TextField
            fullWidth
            label={t('memberName')}
            error={!!errorUserName}
            helperText={!!errorUserName && t('defaultError')}
            {...field}
          />
        )}
      />
    </Stack>
  );
};

export default EmailValidation;
